import { WorkspaceType, ComplianceFramework } from '@deepguide-ai/types'
import {
  BookOpen,
  Shield,
  ShieldCheck,
  GraduationCap,
  Headset,
  Code2,
  FileText,
  LucideIcon
} from 'lucide-react'

/**
 * Workspace Type Configuration
 * Defines type-specific defaults, icons, and onboarding content
 */

export interface WorkspaceTypeConfig {
  type: WorkspaceType
  label: string
  description: string
  icon: LucideIcon
  iconColor: string

  // Onboarding content
  onboarding: {
    title: string
    subtitle: string
    completeTitle: string
    completeSubtitle: string

    // Field-specific guidance
    applicationUrlLabel?: string
    applicationUrlPlaceholder?: string
    applicationUrlHelp?: string

    publicLandingUrlLabel?: string
    publicLandingUrlPlaceholder?: string
    publicLandingUrlHelp?: string
  }

  // Default settings
  defaults: {
    enabledFrameworks?: ComplianceFramework[]
    enabledFeatures?: string[]
  }

  // Workspace-specific terminology
  terminology: {
    // What we call the main content entity
    entity: string           // e.g., "Doc", "Evidence", "Lesson"
    entityPlural: string     // e.g., "Docs", "Evidence", "Lessons"

    // Action buttons
    createAction: string     // e.g., "Create Doc", "Document Control", "Create Lesson"
    viewAction: string       // e.g., "View Doc", "Review Evidence", "View Lesson"
    editAction: string       // e.g., "Edit Doc", "Update Evidence", "Edit Lesson"

    // Additional context
    createTooltip?: string   // Tooltip for create button
    viewTooltip?: string     // Tooltip for view button

    // RFC-087: Approval workflow terminology
    exportNoun?: string      // e.g., "Evidence Pack", "Publication", "Export"
    approvalNoun?: string    // e.g., "Verification", "Review", "Approval"
    approvalVerb?: string    // e.g., "Verify", "Review", "Approve"
    reviewerRole?: string    // e.g., "Auditor", "Reviewer", "Approver"
  }

  // Initial content configuration
  initialContent: {
    title: string          // Title of first topic (e.g., "Getting Started", "Evidence Collection Guide")
    description: string    // Description of the topic
    groupTitle?: string    // Optional group/folder name (e.g., "Quick Start", "Setup")
  }

  // Suggested next steps after creation
  nextSteps: {
    title: string
    description: string
    action: string
  }[]
}

export const WORKSPACE_TYPE_CONFIGS: Record<WorkspaceType, WorkspaceTypeConfig> = {
  DOCUMENTATION: {
    type: 'DOCUMENTATION',
    label: 'Documentation',
    description: 'Create and maintain product documentation, user guides, and help articles',
    icon: BookOpen,
    iconColor: 'text-blue-600',

    onboarding: {
      title: 'Create a Documentation Workspace',
      subtitle: 'Build comprehensive documentation for your product',
      completeTitle: 'Complete your documentation setup',
      completeSubtitle: 'Set the basics to start creating your docs',

      applicationUrlLabel: 'Application URL',
      applicationUrlPlaceholder: 'https://app.example.com',
      applicationUrlHelp: "Your application's home URL. Screen recordings will start from here.",

      publicLandingUrlLabel: 'Public Documentation Site',
      publicLandingUrlPlaceholder: 'https://example.com',
      publicLandingUrlHelp: 'Your public website. We\'ll use this to build a knowledge base for AI assistance.',
    },

    defaults: {
      enabledFrameworks: [],
      enabledFeatures: [],
    },

    terminology: {
      entity: 'Doc',
      entityPlural: 'Docs',
      createAction: 'Create Doc',
      viewAction: 'View Doc',
      editAction: 'Edit Doc',
      createTooltip: 'Create a new documentation page',
      viewTooltip: 'View this documentation',
      // RFC-087: Approval terminology
      exportNoun: 'Publication',
      approvalNoun: 'Review',
      approvalVerb: 'Review',
      reviewerRole: 'Reviewer',
    },

    initialContent: {
      groupTitle: 'Quick Start',
      title: 'Getting Started',
      description: 'Welcome to your documentation workspace! Learn how to create and organize your docs.',
    },

    nextSteps: [
      {
        title: 'Record your first workflow',
        description: 'Use the browser extension to capture screen recordings',
        action: 'Start Recording',
      },
      {
        title: 'Import existing docs',
        description: 'Connect Notion, Confluence, or GitHub to import content',
        action: 'Import Sources',
      },
      {
        title: 'Organize content',
        description: 'Create sections and organize your documentation structure',
        action: 'Manage Structure',
      },
    ],
  },

  COMPLIANCE: {
    type: 'COMPLIANCE',
    label: 'Compliance',
    description: 'Track compliance frameworks, manage evidence, and automate audit preparation',
    icon: Shield,
    iconColor: 'text-green-600',

    onboarding: {
      title: 'Create a Compliance Workspace',
      subtitle: 'Manage compliance frameworks and evidence automation',
      completeTitle: 'Complete your compliance setup',
      completeSubtitle: 'Configure your systems for automated evidence collection',

      applicationUrlLabel: 'System Under Assessment',
      applicationUrlPlaceholder: 'https://app.example.com',
      applicationUrlHelp: 'The application or system being assessed for compliance. Screenshots and evidence will be collected here.',

      publicLandingUrlLabel: 'Organization Website',
      publicLandingUrlPlaceholder: 'https://company.com',
      publicLandingUrlHelp: 'Your organization\'s public website. Used for policy documentation and organizational context.',
    },

    defaults: {
      enabledFrameworks: [], // Empty - user selects frameworks during setup
      enabledFeatures: ['COMPLIANCE_AUTOMATION'],
    },

    terminology: {
      entity: 'Evidence',
      entityPlural: 'Evidence',
      createAction: 'Document Control',
      viewAction: 'Review Evidence',
      editAction: 'Update Evidence',
      createTooltip: 'Create evidence documentation for this control',
      viewTooltip: 'Review all evidence and documentation for this control',
      // RFC-087: Approval terminology
      exportNoun: 'Evidence Pack',
      approvalNoun: 'Verification',
      approvalVerb: 'Verify',
      reviewerRole: 'Auditor',
    },

    initialContent: {
      groupTitle: 'Compliance Setup',
      title: 'Evidence Collection Guide',
      description: 'Learn how to collect and organize evidence for your compliance frameworks (SOC2, CMMC, ISO27001).',
    },

    nextSteps: [
      {
        title: 'Choose compliance frameworks',
        description: 'Enable SOC2, CMMC, ISO27001, or other frameworks',
        action: 'Configure Frameworks',
      },
      {
        title: 'Map existing evidence',
        description: 'Import and map evidence to compliance controls',
        action: 'Import Evidence',
      },
      {
        title: 'Set up automation',
        description: 'Configure automated evidence collection and monitoring',
        action: 'Setup Automation',
      },
    ],
  },

  TRAINING: {
    type: 'TRAINING',
    label: 'Training',
    description: 'Create training materials, tutorials, and onboarding content for your team',
    icon: GraduationCap,
    iconColor: 'text-purple-600',

    onboarding: {
      title: 'Create a Training Workspace',
      subtitle: 'Build interactive training materials and tutorials',
      completeTitle: 'Complete your training setup',
      completeSubtitle: 'Set up your training environment',

      applicationUrlLabel: 'Training Application URL',
      applicationUrlPlaceholder: 'https://training.example.com',
      applicationUrlHelp: 'The application used for training. Record step-by-step tutorials here.',

      publicLandingUrlLabel: 'Public Course Catalog',
      publicLandingUrlPlaceholder: 'https://learn.example.com',
      publicLandingUrlHelp: 'Your public training or learning portal (optional).',
    },

    defaults: {
      enabledFrameworks: [],
      enabledFeatures: ['VIDEO_TUTORIALS', 'KNOWLEDGE_BASE'],
    },

    terminology: {
      entity: 'Lesson',
      entityPlural: 'Lessons',
      createAction: 'Create Lesson',
      viewAction: 'View Lesson',
      editAction: 'Edit Lesson',
      createTooltip: 'Create a new training lesson or tutorial',
      viewTooltip: 'View this training lesson',
      // RFC-087: Approval terminology
      exportNoun: 'Training Package',
      approvalNoun: 'Certification',
      approvalVerb: 'Certify',
      reviewerRole: 'Instructor',
    },

    initialContent: {
      groupTitle: 'Onboarding',
      title: 'Welcome to Training',
      description: 'Start building your training materials and interactive tutorials for your team.',
    },

    nextSteps: [
      {
        title: 'Record training sessions',
        description: 'Capture screen recordings for step-by-step tutorials',
        action: 'Start Recording',
      },
      {
        title: 'Organize learning paths',
        description: 'Structure content into courses and modules',
        action: 'Create Structure',
      },
      {
        title: 'Add assessments',
        description: 'Create quizzes and knowledge checks',
        action: 'Add Assessments',
      },
    ],
  },

  SUPPORT: {
    type: 'SUPPORT',
    label: 'Support',
    description: 'Build a knowledge base for customer support and troubleshooting guides',
    icon: Headset,
    iconColor: 'text-orange-600',

    onboarding: {
      title: 'Create a Support Workspace',
      subtitle: 'Build a knowledge base for customer support',
      completeTitle: 'Complete your support setup',
      completeSubtitle: 'Configure your support knowledge base',

      applicationUrlLabel: 'Product Application URL',
      applicationUrlPlaceholder: 'https://app.example.com',
      applicationUrlHelp: 'Your product URL. Create troubleshooting guides and how-to articles.',

      publicLandingUrlLabel: 'Help Center URL',
      publicLandingUrlPlaceholder: 'https://help.example.com',
      publicLandingUrlHelp: 'Your public help center or support portal.',
    },

    defaults: {
      enabledFrameworks: [],
      enabledFeatures: ['KNOWLEDGE_BASE'],
    },

    terminology: {
      entity: 'Article',
      entityPlural: 'Articles',
      createAction: 'Create Article',
      viewAction: 'View Article',
      editAction: 'Edit Article',
      createTooltip: 'Create a new support article or troubleshooting guide',
      viewTooltip: 'View this support article',
      // RFC-087: Approval terminology
      exportNoun: 'Knowledge Export',
      approvalNoun: 'Review',
      approvalVerb: 'Review',
      reviewerRole: 'Editor',
    },

    initialContent: {
      groupTitle: 'Help Center',
      title: 'Knowledge Base Setup',
      description: 'Build your customer support knowledge base with troubleshooting guides and FAQs.',
    },

    nextSteps: [
      {
        title: 'Create troubleshooting guides',
        description: 'Document common issues and solutions',
        action: 'Create Guides',
      },
      {
        title: 'Import existing articles',
        description: 'Bring in content from Zendesk, Intercom, or other platforms',
        action: 'Import Content',
      },
      {
        title: 'Set up AI assistance',
        description: 'Enable AI-powered support article suggestions',
        action: 'Configure AI',
      },
    ],
  },

  API_DOCS: {
    type: 'API_DOCS',
    label: 'API Documentation',
    description: 'Document APIs, endpoints, and integration guides for developers',
    icon: Code2,
    iconColor: 'text-cyan-600',

    onboarding: {
      title: 'Create an API Documentation Workspace',
      subtitle: 'Document your API for developers',
      completeTitle: 'Complete your API docs setup',
      completeSubtitle: 'Configure your API documentation environment',

      applicationUrlLabel: 'API Base URL',
      applicationUrlPlaceholder: 'https://api.example.com',
      applicationUrlHelp: 'Your API base URL. Used for generating endpoint examples.',

      publicLandingUrlLabel: 'Developer Portal',
      publicLandingUrlPlaceholder: 'https://developers.example.com',
      publicLandingUrlHelp: 'Your public developer portal or API documentation site.',
    },

    defaults: {
      enabledFrameworks: [],
      enabledFeatures: ['API_DOCUMENTATION'],
    },

    terminology: {
      entity: 'Endpoint',
      entityPlural: 'Endpoints',
      createAction: 'Document Endpoint',
      viewAction: 'View Endpoint',
      editAction: 'Edit Endpoint',
      createTooltip: 'Document a new API endpoint or integration guide',
      viewTooltip: 'View API endpoint documentation',
      // RFC-087: Approval terminology
      exportNoun: 'API Release',
      approvalNoun: 'Review',
      approvalVerb: 'Review',
      reviewerRole: 'Tech Lead',
    },

    initialContent: {
      groupTitle: 'API Reference',
      title: 'API Documentation Guide',
      description: 'Learn how to document your API endpoints, authentication, and integration examples.',
    },

    nextSteps: [
      {
        title: 'Import OpenAPI spec',
        description: 'Generate documentation from your OpenAPI/Swagger file',
        action: 'Import Spec',
      },
      {
        title: 'Document endpoints',
        description: 'Add descriptions, examples, and authentication guides',
        action: 'Document APIs',
      },
      {
        title: 'Create integration guides',
        description: 'Write tutorials for common integration scenarios',
        action: 'Write Guides',
      },
    ],
  },

  SCREENATA: {
    type: 'SCREENATA',
    label: 'Screenata',
    description: 'AI-powered SOC 2 compliance program management for startups',
    icon: ShieldCheck,
    iconColor: 'text-emerald-600',

    onboarding: {
      title: 'Start Your SOC 2 Program',
      subtitle: 'Screenata guides you through the entire SOC 2 lifecycle with AI',
      completeTitle: 'Set up your compliance program',
      completeSubtitle: 'Connect your systems so Screenata can automate evidence collection',

      applicationUrlLabel: 'Product URL',
      applicationUrlPlaceholder: 'https://app.yourcompany.com',
      applicationUrlHelp: 'Your main product URL. Screenata will reference this in policies.',

      publicLandingUrlLabel: 'Company Website',
      publicLandingUrlPlaceholder: 'https://yourcompany.com',
      publicLandingUrlHelp: 'Your company website for organizational context.',
    },

    defaults: {
      enabledFrameworks: [],
      enabledFeatures: ['COMPLIANCE_AUTOMATION'],
    },

    terminology: {
      entity: 'Control',
      entityPlural: 'Controls',
      createAction: 'Add Policy',
      viewAction: 'Review Control',
      editAction: 'Update Policy',
      createTooltip: 'Generate a new compliance policy',
      viewTooltip: 'Review control evidence and readiness',
      exportNoun: 'Audit Package',
      approvalNoun: 'Readiness',
      approvalVerb: 'Verify',
      reviewerRole: 'Auditor',
    },

    initialContent: {
      groupTitle: 'SOC 2 Program',
      title: 'Welcome to Screenata',
      description: 'Your AI compliance program manager. Start a conversation to begin your SOC 2 journey.',
    },

    nextSteps: [
      {
        title: 'Connect GitHub',
        description: 'Install the GitHub App to scan repository settings and branch protection',
        action: 'Connect GitHub',
      },
      {
        title: 'Run first scan',
        description: 'Scan your infrastructure for compliance evidence',
        action: 'Start Scan',
      },
      {
        title: 'Generate policies',
        description: 'AI-generate tailored policies based on how you actually operate',
        action: 'Generate Policies',
      },
    ],
  },

  RUNBOOKS: {
    type: 'RUNBOOKS',
    label: 'Runbooks',
    description: 'Create operational runbooks, SOPs, and incident response procedures',
    icon: FileText,
    iconColor: 'text-red-600',

    onboarding: {
      title: 'Create a Runbooks Workspace',
      subtitle: 'Document operational procedures and SOPs',
      completeTitle: 'Complete your runbooks setup',
      completeSubtitle: 'Set up your operational documentation',

      applicationUrlLabel: 'Operations Dashboard',
      applicationUrlPlaceholder: 'https://ops.example.com',
      applicationUrlHelp: 'Your operations dashboard or monitoring system.',

      publicLandingUrlLabel: 'Status Page',
      publicLandingUrlPlaceholder: 'https://status.example.com',
      publicLandingUrlHelp: 'Your public status page (optional).',
    },

    defaults: {
      enabledFrameworks: [],
      enabledFeatures: ['WORKFLOW_AUTOMATION', 'CHANGE_MANAGEMENT'],
    },

    terminology: {
      entity: 'Runbook',
      entityPlural: 'Runbooks',
      createAction: 'Create Runbook',
      viewAction: 'View Runbook',
      editAction: 'Edit Runbook',
      createTooltip: 'Create a new operational runbook or SOP',
      viewTooltip: 'View this runbook',
      // RFC-087: Approval terminology
      exportNoun: 'Change Request',
      approvalNoun: 'Approval',
      approvalVerb: 'Approve',
      reviewerRole: 'Operations Lead',
    },

    initialContent: {
      groupTitle: 'Operations',
      title: 'Runbook Template',
      description: 'Create standard operating procedures and incident response runbooks for your team.',
    },

    nextSteps: [
      {
        title: 'Create incident runbooks',
        description: 'Document incident response procedures',
        action: 'Create Runbooks',
      },
      {
        title: 'Document SOPs',
        description: 'Write standard operating procedures',
        action: 'Write SOPs',
      },
      {
        title: 'Set up change management',
        description: 'Track changes and deployment procedures',
        action: 'Setup Changes',
      },
    ],
  },
}

/**
 * Get workspace type configuration
 */
export function getWorkspaceTypeConfig(type: WorkspaceType): WorkspaceTypeConfig {
  return WORKSPACE_TYPE_CONFIGS[type] || WORKSPACE_TYPE_CONFIGS.DOCUMENTATION
}

/**
 * Get icon component for workspace type
 */
export function getWorkspaceTypeIcon(type: WorkspaceType): LucideIcon {
  return getWorkspaceTypeConfig(type).icon
}

/**
 * Get icon color class for workspace type
 */
export function getWorkspaceTypeIconColor(type: WorkspaceType): string {
  return getWorkspaceTypeConfig(type).iconColor
}
